/**
 * @providesModule TrophyToastDataController
 */

import CurrentUserStore from 'CurrentUserStore';
import Prefs from 'Preferences';
import SKZConstants from 'SKZConstants';
import SKZTrophy, {TrophyRecord} from 'SKZTrophy';
import TrophyDataController from 'TrophyDataController';


/**
 * Function that gets all of the trophy data and stores it in Async storage.
 * Currently only used on joining a match, but can be used elsewhere in the future (e.g. on deposit)
 */
export const setTrophyDataForStorage = async () => {
  const trophyDataFromStorage = getTrophyDataFromStorage();
  const refreshExpiryTime = Prefs.get(SKZConstants.SKZ_TROPHIES_REFRESH_EXPIRY_TIME);
  if (trophyDataFromStorage.length > 0 && refreshExpiryTime - Date.now() > 0) {
    return;
  }
  const newTrophies = await fetchTrophyData();
  Prefs.set({
    [SKZConstants.SKZ_TROPHIES_DATA]: newTrophies,
    [SKZConstants.SKZ_TROPHIES_REFRESH_EXPIRY_TIME]: Date.now() + 60 * 5 * 1000, // 5 minutes
  });
};

/**
 * Function that gets the user's trophy data from Async storage
 * @returns Array of TrophyRecords
 */
export const getTrophyDataFromStorage = (): TrophyRecord[] => {
  const trophyData: TrophyRecord[] | undefined | null = Prefs.get(SKZConstants.SKZ_TROPHIES_DATA);
  // Edge case check if local storage hasn't been set yet
  if (!trophyData) {
    const emptyTrophyData: TrophyRecord[] = [];
    return emptyTrophyData;
  }
  return trophyData;
};

/**
 * Function that gets the current user's trophies and returns them. If there is an error, returns [].
 * Also clears the cache for the trophies call so that we get current data on next trophy call
 * @returns Array of Trophies
 */
export const fetchTrophyData = async () => {
  try {
    const userId = await CurrentUserStore.getCurrentUserId();
    const trophyData = await TrophyDataController.fetchTrophiesForUser(
      userId,
      true,
      SKZConstants.SKZ_TROPHIES_FILTER_ALL_V2,
      true
    );
    TrophyDataController.clearTrophiesCacheForUser(userId);
    if (Array.isArray(trophyData)) {
      const trophies = SKZTrophy.make(trophyData);
      return trophies;
    } else {
      return [];
    }
  } catch (error) {
    console.debug('TrophyToastDataController: fetch trophies failed');
    return [];
  }
};

/**
 * Function that takes in arrays of Trophies at two different times (e.g. before a match and after score submit)
 * and determines what individual trophies from the new array to show. Also limits the amount of trophies
 * that can be returned, to a value set by Product.
 * @param trophiesAfter Current array of Trophies that we want to check progress on
 * @param trophiesBefore Previous array of Trophies that we want as our baseline
 * @returns Array of Trophies that have progressed significantly enough that we want to show the user
 */
export const getProgressedTrophiesToToast = (
  trophiesAfter: Array<SKZTrophy>,
  trophiesBefore: TrophyRecord[],
  addExtraCheckpoint = false
): Array<SKZTrophy> => {
  const trophiesToToast: Array<SKZTrophy> = [];

  const maxTrophyToastsCount = 3;

  // If something weird happened and we don't have trophy data from when we stored it, return an empty array
  if (!trophiesBefore || trophiesBefore.length === 0) {
    return trophiesToToast;
  }

  // For H2H revamped test path, use most significant logic and return only 1 trophy
  if (addExtraCheckpoint) {
    return getMostSignificantTrophiesToToast(trophiesAfter, trophiesBefore, 1); // Only 1 trophy for H2H revamped
  }

  // Original first-come, first-served logic
  for (const currentTrophy of trophiesAfter) {
    // Check if there's an id match between trophy arrays before and after score submit
    const matchingTrophy = trophiesBefore.find((obj) => obj.id === currentTrophy.id);

    // If there is a change in the trophy progress, or the currentTrophy doesn't exist in
    // trophiesBefore array (as it's a new or hidden trophy), then that's a trophy to check if we should show it
    if (!matchingTrophy || matchingTrophy.total_progress !== currentTrophy.total_progress) {
      // If the trophy has just been awarded or passed a new checkpoint, show it
      if (shouldShowTrophyToast(currentTrophy, matchingTrophy)) {
        trophiesToToast.push(currentTrophy);
      }
    }
    // Limit number of toasts shown to maxTrophyToastsCount
    if (trophiesToToast.length === maxTrophyToastsCount) {
      break;
    }
  }

  return trophiesToToast;
};

/**
 * Function that implements "most significant" trophy selection logic for H2H revamped test path.
 * This overrides the first-come, first-served approach with intelligent ranking based on:
 * 1. Priority: Awarded trophies first
 * 2. Progress significance: Higher percentage gains get priority
 * 3. Checkpoint importance: Higher checkpoints (90% > 50% > 25%) get priority
 *
 * @param trophiesAfter Current array of Trophies
 * @param trophiesBefore Previous array of Trophies
 * @param maxCount Maximum number of trophies to return
 * @returns Array of Trophies ranked by significance
 */
export const getMostSignificantTrophiesToToast = (
  trophiesAfter: Array<SKZTrophy>,
  trophiesBefore: TrophyRecord[],
  maxCount: number
): Array<SKZTrophy> => {
  const eligibleTrophies: Array<{trophy: SKZTrophy; score: number}> = [];

  // Find all eligible trophies and calculate their significance scores
  for (const currentTrophy of trophiesAfter) {
    const matchingTrophy = trophiesBefore.find((obj) => obj.id === currentTrophy.id);

    if (!matchingTrophy || matchingTrophy.total_progress !== currentTrophy.total_progress) {
      if (shouldShowTrophyToast(currentTrophy, matchingTrophy, true)) {
        const score = calculateTrophySignificanceScore(currentTrophy, matchingTrophy);
        eligibleTrophies.push({trophy: currentTrophy, score});
      }
    }
  }

  // Early return if no eligible trophies
  if (eligibleTrophies.length === 0) {
    return [];
  }

  // Sort by significance score (highest first) and return top trophies
  return eligibleTrophies
    .sort((a, b) => b.score - a.score)
    .slice(0, maxCount)
    .map((item) => item.trophy);
};

/**
 * Calculates a significance score for a trophy based on multiple factors:
 * - Awarded trophies get highest priority (score 1000+)
 * - Progress percentage gains get weighted scores
 * - Checkpoint importance affects scoring
 *
 * @param trophyAfter Current trophy state
 * @param trophyBefore Previous trophy state
 * @returns Numerical score representing trophy significance
 */
const calculateTrophySignificanceScore = (
  trophyAfter: SKZTrophy,
  trophyBefore?: TrophyRecord
): number => {
  // Awarded trophies get highest priority
  if (trophyAfter.isAwarded()) {
    return 1000;
  }

  const trophyProgressAfter = parseFloat(trophyAfter.total_progress);
  const trophyProgressBefore = trophyBefore ? parseFloat(trophyBefore.total_progress) : 0;
  const progressGain = trophyProgressAfter - trophyProgressBefore;

  // Calculate progress percentage gain
  const progressPercentageGain = (progressGain / trophyAfter.goal) * 100;

  // Base score from progress gain (higher gains = higher scores)
  let score = progressPercentageGain * 10;

  // Bonus points for hitting important checkpoints
  const checkpoints = [0.1, 0.25, 0.5, 0.9];
  for (const checkpoint of checkpoints) {
    const checkpointValue = trophyAfter.goal * checkpoint;
    if (trophyProgressBefore < checkpointValue && trophyProgressAfter >= checkpointValue) {
      // Higher checkpoints get more bonus points
      if (checkpoint === 0.9)
        score += 50; // 90% checkpoint
      else if (checkpoint === 0.5)
        score += 30; // 50% checkpoint
      else if (checkpoint === 0.25)
        score += 20; // 25% checkpoint
      else if (checkpoint === 0.1) score += 10; // 10% checkpoint
    }
  }

  return score;
};

/**
 * Function that takes two trophies with matching IDs (or just a new trophy that didn't exist in the previous state)
 * and checks if that trophy has made significant enough progress to show it to the user. The checkpoints to
 * pass for the individual trophy are set by Product
 * @param trophyAfter Current Trophy that we want to check progress on
 * @param trophyBefore Matching Trophy in the old state that we want as our baseline
 * @returns boolean: true if we want to show the user the trophy, false if we do not
 */
export const shouldShowTrophyToast = (
  trophyAfter: SKZTrophy,
  trophyBefore?: TrophyRecord,
  increasePercentage = false
): boolean => {
  // If we've been awarded the trophy after playing, show it: no need to check progress
  if (trophyAfter.isAwarded()) {
    return true;
  }

  const percentageCheckpoints = increasePercentage ? [0.1, 0.25, 0.5, 0.9] : [0.25, 0.5, 0.9]; // eslint-disable-line no-magic-numbers

  // Loop through the checkpoints
  for (const checkpoint of percentageCheckpoints) {
    // NOTE: the `goal` for trophyBefore and trophyAfter should be the same, but use each trophy's goal
    // to calculate progressCheckpoint in the rare case that the goal amount has changed.
    const progressCheckpointAfter = trophyAfter.goal * checkpoint;
    const progressCheckpointBefore = trophyBefore
      ? trophyBefore.goal * checkpoint
      : progressCheckpointAfter;

    const trophyProgressAfter = parseFloat(trophyAfter.total_progress);
    const trophyProgressBefore = trophyBefore ? parseFloat(trophyBefore.total_progress) : 0; // Setting this to zero when there is no trophyBefore means that the first part of the conditional below will always pass, which is what we want b/c there's no trophy to compare to

    // If the progressBefore is less than the checkpoint, and the progressAfter is greater than the checkpoint,
    // then we passed a new checkpoint. Note that we don't actually care how many checkpoints we clear, just that we
    // cleared at least one, since clearing just one checkpoint should prompt the toast
    if (
      trophyProgressBefore < progressCheckpointBefore &&
      trophyProgressAfter >= progressCheckpointAfter
    ) {
      return true;
    }
  }
  // If we haven't hit a new checkpoint, don't show toast
  return false;
};
